import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { waLink } from '@/config/site';
import { LOGO_CINZA } from '@/lib/projetos';

interface NavbarProps {
  /** Mantido por compatibilidade com páginas que exibem barra de aviso. */
  announcementHeight?: number;
}

const navLinks = [
  { label: 'Soluções', path: '/produtos' },
  { label: 'Projetos', path: '/cases' },
  { label: 'Órgãos Públicos', path: '/fornecimento-orgaos-publicos' },
  { label: 'Catálogo', path: '/catalogo' },
  { label: 'Contato', path: '/contato' },
];

export default function Navbar({ announcementHeight = 0 }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-[background-color,border-color] duration-300 backdrop-blur-xl backdrop-saturate-150 ${
          scrolled || menuOpen
            ? 'bg-white/80 border-b border-black/[0.06]'
            : 'bg-white/70 border-b border-transparent'
        }`}
        style={{ top: `${announcementHeight}px` }}
      >
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center flex-shrink-0" aria-label="HomeOffice Premium — página inicial">
            <img src={LOGO_CINZA} alt="HomeOffice Premium" className="h-[30px] w-[140px] md:h-[36px] md:w-[170px] object-cover" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Navegação principal">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[13px] tracking-[-0.01em] whitespace-nowrap transition-colors ${
                  isActive(link.path) ? 'text-[#1D1D1F]' : 'text-[#1D1D1F]/70 hover:text-[#1D1D1F]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <a
              href={waLink('projetos')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-[#E8610A] text-white text-[12.5px] font-medium px-4 py-[7px] rounded-full hover:bg-[#C4500A] whitespace-nowrap"
            >
              Fale com o Neto
            </a>
          </div>

          <button
            className="lg:hidden -mr-2 w-10 h-10 flex items-center justify-center text-[#1D1D1F] rounded-full"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
          >
            <span className="relative block w-[18px] h-[12px]" aria-hidden="true">
              <span
                className={`absolute left-0 right-0 h-[1.5px] bg-current rounded transition-transform duration-300 ${
                  menuOpen ? 'top-[5px] rotate-45' : 'top-[2px]'
                }`}
              />
              <span
                className={`absolute left-0 right-0 h-[1.5px] bg-current rounded transition-transform duration-300 ${
                  menuOpen ? 'top-[5px] -rotate-45' : 'top-[9px]'
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-14" style={{ top: `${announcementHeight}px` }}>
          <nav className="px-8 pt-6 pb-10 flex flex-col" aria-label="Navegação mobile">
            {navLinks.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className="py-2.5 text-[28px] font-semibold tracking-[-0.03em] text-[#1D1D1F] animate-reveal"
                style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={waLink('projetos')}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center bg-[#E8610A] text-white text-[15px] font-medium px-6 py-3.5 rounded-full"
            >
              Fale com o Neto no WhatsApp
            </a>
            <p className="mt-8 text-[13px] text-[#6E6E73] leading-relaxed">
              Showroom com hora marcada
              <br />
              Av. Agamenon Magalhães, 1519 — Caruaru, PE
            </p>
          </nav>
        </div>
      )}
    </>
  );
}
